
import { IconGripVertical, IconTrash } from '@tabler/icons-react';
import NSIcon from 'bricks/NSIcon/NSIcon';
import { useState } from 'react';
import classNames from 'classnames';
import { NSButton } from 'bricks';
import { useDndMonitor } from '@dnd-kit/core';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
    IProFormaUses, ISelectedProjection, ProjectionType, DevelopmentUnitOfMeasureTypes,
    IBudgetClassificationPatchData, IFormattedCashFlowPeriod,
} from 'views/ProFormaTable/types';
import { numeralFormatterCurrency, formatISOStartOfMonth } from 'ns_libs/formatter';
import RenderIf from 'components/RenderIf/RenderIf';
import { URLS } from 'services/urls';
import {
    BudgetClassificationTypeEnum, EDITABLE_BUDGET_CLASSIFICATION_TYPES, PROJECTION_TYPE, PROJECTION_TYPES,
} from 'views/ProFormaTable/constants';
import { PRO_FORMA_UNIT_TO_FIELD_VALUES, UNIT_OF_MEASURE_TYPES } from 'constants/unitTypes';
import NSCellInput from 'bricks/NSCellInput/NSCellInput';
import { Periods } from 'components/ProFormaReturnsTable/constant';
import { useProFormaSocketContext } from 'views/ProFormaTable/components/ProForma/socketContext/ProFormaSocketProvider';
import { formatCashFlowPeriods, getPeriodsOptions } from 'views/ProFormaTable/helpers';
import ConfirmationModal from 'components/ConfirmationModal/ConfirmationModal';
import PeriodsSelect from '../../../../../PeriodsSelect/PeriodsSelect';
import CurveFitModal from '../../../../CurveFitModal/CurveFitModal';
import { useUsesTableContext } from '../../../context/UsesTableProvider';
import PeriodCellValue from '../../../PeriodCellValue/PeriodCellValue';
import UsesTableBudgetLineNameCell from './UsesTableBudgetLineNameCell/UsesTableBudgetLineNameCell';
import './UsesTableBudgetLine.scss';

export interface IBudgetLineParentDragStyle {
    transform: string | undefined;
    transition: string | undefined;
    opacity: number;
}

export interface IUsesTableBudgetLine {
    budgetClassification: IProFormaUses;
    unitType: DevelopmentUnitOfMeasureTypes;
    projectionPeriods: IFormattedCashFlowPeriod[];
    isPreview?: boolean;
    isSortable?: boolean;
    parentDragStyle?: IBudgetLineParentDragStyle;
}

const UsesTableBudgetLine = ({
    budgetClassification, unitType, isPreview, projectionPeriods, isSortable = true, parentDragStyle,
}: IUsesTableBudgetLine) => {
    const {
        proForma,
        handleUpdateBudgetClassifications,
        isBudgetClassificationUpdatingLoading,
        handleDeleteBudgetClassification,
        isBudgetClassificationDeletionLoading,
        showCents,
        cashFlowPeriodsByCadence,
        periodCadence,
    } = useProFormaSocketContext();

    const { selectedCategories, isProFormaReadOnly } = useUsesTableContext();
    const [isHovered, setIsHovered] = useState(false);

    const canDrag = isSortable && !isPreview && !isProFormaReadOnly;

    const {
        attributes, listeners, setNodeRef, transform, transition, isDragging,
    } = useSortable({ id: budgetClassification.id, disabled: !canDrag });

    // Override dnd-kit's snap-to-slot transform with the raw pointer delta on the active
    // row to avoid the mid-drag visual jump when items reorder.
    const [rawPointerDelta, setRawPointerDelta] = useState<{ x: number; y: number } | null>(null);

    useDndMonitor({
        onDragMove(event) {
            if (event.active.id === budgetClassification.id) setRawPointerDelta(event.delta);
        },
        onDragEnd(event) {
            if (event.active.id === budgetClassification.id) setRawPointerDelta(null);
        },
        onDragCancel(event) {
            if (event.active.id === budgetClassification.id) setRawPointerDelta(null);
        },
    });

    const ownTransformStyle = isDragging && rawPointerDelta
        ? `translate3d(0px, ${rawPointerDelta.y}px, 0)`
        : CSS.Transform.toString(transform);

    const ownTransition = isDragging ? undefined : transition;

    // Follow the parent subcategory's transform when it's being dragged or shifted, so
    // the whole group moves together; otherwise use this row's own sortable transform.
    const rowStyle = parentDragStyle
        || (canDrag
            ? { transform: ownTransformStyle, transition: ownTransition, opacity: isDragging ? 0.6 : 1 }
            : undefined);
    const [curveFitModalOpen, setCurveFitModalOpen] = useState(false);
    const [openDeleteBudgetLineModal, setOpenDeleteBudgetLineModal] = useState(false);

    const selectedUnitTotal = budgetClassification[PRO_FORMA_UNIT_TO_FIELD_VALUES[unitType]];

    const toggleDeleteBudgetLineModal = () => setOpenDeleteBudgetLineModal(!openDeleteBudgetLineModal);
    const toggleCurveFitModal = () => setCurveFitModalOpen(!curveFitModalOpen);

    const selectedProjectionsCurveList = PROJECTION_TYPES.filter(projection => projection.projectionType === budgetClassification.projectionType);
    const selectedCurveByVariable = selectedProjectionsCurveList?.find(
        projection => projection.projectionVariable === Number(budgetClassification.projectionVariable),
    );

    let selectedCurve: ISelectedProjection = {
        name: selectedProjectionsCurveList[0]?.curveType,
        value: budgetClassification.projectionVariable || null,
        imgPrimary: selectedProjectionsCurveList[0]?.imgPrimary,
    };

    if (
        budgetClassification.projectionType
        && [PROJECTION_TYPE.NORMAL, PROJECTION_TYPE.GAMMA].includes(budgetClassification.projectionType)
        && selectedCurveByVariable
    ) {
        selectedCurve = {
            name: selectedCurveByVariable.curveType,
            value: budgetClassification.projectionVariable || null,
            imgPrimary: selectedCurveByVariable.imgPrimary,
        };
    }

    const updateSuccessCallback = () => {
        if (curveFitModalOpen) toggleCurveFitModal();
    };

    const updateBudgetClassification = (updatedValues: Partial<IBudgetClassificationPatchData>) => {
        if (budgetClassification.isDeletable || EDITABLE_BUDGET_CLASSIFICATION_TYPES.includes(budgetClassification.classificationType)
            || 'leaseUpIncomePercentage' in updatedValues
        ) {
            const values = { ...updatedValues };
            if ('percentageOfDebt' in values) {
                let value = Number(values.percentageOfDebt);
                value = Math.min(value, 1);
                values.percentageOfDebt = String(value);
                values.calculatedOn = 'percentage_of_debt';
            }
            if ('percentageOfHardCosts' in values) {
                let value = Number(values.percentageOfHardCosts);
                value = Math.min(value, 1);
                values.percentageOfHardCosts = String(value);
                values.calculatedOn = 'percentage_of_hard_costs';
            }
            if ('leaseUpIncomePercentage' in values) {
                let value = Number(values.leaseUpIncomePercentage);

                value = Math.min(value, 1);
                values.leaseUpIncomePercentage = String(value);
            }
            handleUpdateBudgetClassifications([{ id: budgetClassification.id, ...values }], updateSuccessCallback);
        }
    };

    const handleUpdateProjection = (projectionType: ProjectionType, projectionVariable: number | null) => {
        const targetProjection = PROJECTION_TYPES.find(projection => projection.projectionType === projectionType);
        if (targetProjection) {
            updateBudgetClassification({
                projectionType,
                projectionVariable,
            });
        }
    };

    const deleteBudgetLine = () => {
        handleDeleteBudgetClassification([budgetClassification.id], false, toggleDeleteBudgetLineModal);
    };

    const monthlyPeriodOptions = formatCashFlowPeriods(cashFlowPeriodsByCadence, Periods.MONTHLY);

    const periodOptions = getPeriodsOptions(proForma.startDate, proForma.saleDate, monthlyPeriodOptions);

    const hasNoSelectedCategories = Object.values(selectedCategories).every(categoryLevel => !categoryLevel.length);

    let budgetClassificationPeriods = budgetClassification.periods || [];
    if (periodCadence === Periods.ANNUALLY) budgetClassificationPeriods = budgetClassification.yearlyPeriods || [];

    const isInterestReserve = budgetClassification.classificationType === BudgetClassificationTypeEnum.INTEREST_RESERVE;
    const isOperatingReserve = budgetClassification.classificationType === BudgetClassificationTypeEnum.OPERATING_RESERVE;

    const isUneditableBudgetClassification = isInterestReserve || isOperatingReserve;

    const isLineEditable = budgetClassification.isDeletable
        || EDITABLE_BUDGET_CLASSIFICATION_TYPES.includes(budgetClassification.classificationType);

    const isCostPerGrossSquareFootageEditable = unitType === UNIT_OF_MEASURE_TYPES.GROSS_SF
        && !isUneditableBudgetClassification
        && isLineEditable;

    const isCostPerRentableSquareFootageEditable = unitType === UNIT_OF_MEASURE_TYPES.RENTABLE_SF
        && !isUneditableBudgetClassification
        && isLineEditable;

    const editableCostPer = isCostPerGrossSquareFootageEditable
        ? {
            value: budgetClassification.costPerGrossSquareFootage,
            payloadKey: 'costPerGrossSquareFootage' as const,
            calculatedOn: 'cost_per_gross_square_footage' as const,
        }
        : isCostPerRentableSquareFootageEditable
            ? {
                value: budgetClassification.costPerRentableSquareFootage,
                payloadKey: 'costPerRentableSquareFootage' as const,
                calculatedOn: 'cost_per_rentable_square_footage' as const,
            }
            : null;

    return (
        <>
            <tr
                ref={setNodeRef}
                style={rowStyle}
                className="NSTable__tbody__tr NSTable__tbody__tr--level-3"
            >
                <td
                    onMouseEnter={() => setIsHovered(true)}
                    onMouseLeave={() => setIsHovered(false)}
                    className="NSTable__tbody__tr__td NSTable__tbody__tr__td--right-border NSTable__tbody__tr__td__input h-100"
                >
                    <div className="d-flex justify-content-between align-items-center">
                        <div className="d-flex align-items-center flex-grow-1">
                            <UsesTableBudgetLineNameCell
                                budgetClassification={budgetClassification}
                                updateBudgetClassification={updateBudgetClassification}
                                isPreview={isPreview}
                                dragHandle={canDrag ? (
                                    <span
                                        {...attributes}
                                        {...listeners}
                                        aria-label="Drag to reorder budget line"
                                        className={classNames('UsesTableBudgetLine__dragHandle mr-2', { invisible: !isHovered })}
                                    >
                                        <NSIcon icon={IconGripVertical} className="text-muted" />
                                    </span>
                                ) : null}
                            />
                        </div>
                        <RenderIf isTrue={hasNoSelectedCategories && budgetClassification.isDeletable}>
                            <NSButton
                                icon={IconTrash}
                                size="sm"
                                className={classNames('px-2 mr-2 text-muted hover:text-dark bg-none hover:bg-light', { invisible: !isHovered })}
                                callback={() => isHovered && toggleDeleteBudgetLineModal()}
                                isLoading={isBudgetClassificationDeletionLoading}
                            />
                        </RenderIf>
                    </div>
                </td>
                <td className="NSTable__tbody__tr__td NSTable__tbody__tr__td__input">
                    <RenderIf isTrue={budgetClassification.isDeletable
                        || EDITABLE_BUDGET_CLASSIFICATION_TYPES.includes(budgetClassification.classificationType)}
                    >
                        <PeriodsSelect
                            handleChange={value => updateBudgetClassification({ startDate: value })}
                            options={periodOptions}
                            value={!budgetClassification.startDate ? null
                                : formatISOStartOfMonth(budgetClassification.startDate)}
                        />
                    </RenderIf>
                </td>
                <td className="NSTable__tbody__tr__td NSTable__tbody__tr__td__input">
                    <RenderIf isTrue={budgetClassification.isDeletable
                        || EDITABLE_BUDGET_CLASSIFICATION_TYPES.includes(budgetClassification.classificationType)}
                    >
                        <PeriodsSelect
                            handleChange={value => updateBudgetClassification({ endDate: value })}
                            options={periodOptions}
                            value={!budgetClassification.endDate ? null
                                : formatISOStartOfMonth(budgetClassification.endDate)}
                        />
                    </RenderIf>
                </td>
                <td className="NSTable__tbody__tr__td align-middle ">
                    <NSButton
                        className="text-primary cursor--pointer text-nowrap p-0"
                        callback={toggleCurveFitModal}
                        ariaLabel="Edit curve"
                    >
                        <RenderIf isTrue={budgetClassification.isDeletable
                            || EDITABLE_BUDGET_CLASSIFICATION_TYPES.includes(budgetClassification.classificationType)}
                        >
                            <RenderIf isTrue={budgetClassification.projectionType}>
                                <img
                                    src={URLS.IMAGE_CLOUDFRONT_URL(selectedCurve.imgPrimary)}
                                    alt={`selectedCurve-${selectedCurve.name}`}
                                    width="16px"
                                    className="mx-auto"
                                />
                                <small className="mt-0 ml-1">{selectedCurve.name}</small>
                            </RenderIf>
                            <RenderIf isTrue={!budgetClassification.projectionType}>
                                <span className="text-primary text-nowrap font-italic">+ Add</span>
                            </RenderIf>
                        </RenderIf>
                    </NSButton>
                </td>
                <td className="NSTable__tbody__tr__td align-middle">
                    {editableCostPer ? (
                        <div className="d-flex align-items-center justify-content-end">
                            <NSCellInput
                                type="number"
                                numberType="currency"
                                value={editableCostPer.value}
                                handleUpdateValue={updatedValue => {
                                    updateBudgetClassification({
                                        [editableCostPer.payloadKey]: String(updatedValue),
                                        calculatedOn: editableCostPer.calculatedOn,
                                    });
                                }}
                                placeholder="Enter cost"
                                isRightAligned
                                showCents
                            />
                        </div>
                    ) : (
                        <div className="d-flex justify-content-end text-dark">
                            {selectedUnitTotal != null ? numeralFormatterCurrency(selectedUnitTotal) : '—'}
                        </div>
                    )}
                </td>
                <td className="NSTable__tbody__tr__td NSTable__tbody__tr__td--right-border
                NSTable__tbody__tr__td__input NSTable__tbody__tr__td--width-100"
                >
                    <div className="d-flex align-items-center justify-content-end">
                        <NSCellInput
                            type="number"
                            numberType="currency"
                            value={budgetClassification.amount}
                            handleUpdateValue={updatedValue => {
                                updateBudgetClassification({
                                    amount: String(updatedValue),
                                    calculatedOn: 'amount'
                                });
                            }}
                            placeholder={isUneditableBudgetClassification ? '—' : 'Enter amount'}
                            isRightAligned
                            customClassName={classNames('mr-1', {
                                'text-dark ': isUneditableBudgetClassification,
                            })}
                            disabled={isUneditableBudgetClassification}
                            showCents={showCents}
                        />
                    </div>
                </td>
                {projectionPeriods?.map((period, index) => {
                    const targetPeriod = budgetClassificationPeriods[index];

                    return (
                        <PeriodCellValue
                            key={`budget-line-period-${period.value}`}
                            tooltipId={`budget-line-period-${index}`}
                            period={period}
                            value={targetPeriod?.projected === null || budgetClassification.amount === null
                                ? '—' : numeralFormatterCurrency(targetPeriod?.projected, showCents ? '$0,0.00' : '$0,0')}
                            cellClassName="NSTable__tbody__tr__td NSTable__tbody__tr__td--width-100 text-dark text-right"
                        />
                    );
                })}
            </tr>

            <RenderIf isTrue={curveFitModalOpen}>
                <CurveFitModal
                    toggle={toggleCurveFitModal}
                    isOpen={curveFitModalOpen}
                    headerText="Curve"
                    handleUpdateProjection={handleUpdateProjection}
                    isLoadingCallback={isBudgetClassificationUpdatingLoading}
                    selectedCurve={selectedCurveByVariable}
                    projectionVariable={budgetClassification?.projectionVariable}
                />
            </RenderIf>

            <RenderIf isTrue={openDeleteBudgetLineModal}>
                <ConfirmationModal
                    toggle={toggleDeleteBudgetLineModal}
                    isOpen={openDeleteBudgetLineModal}
                    onConfirm={deleteBudgetLine}
                    onCancel={toggleDeleteBudgetLineModal}
                    modalHeaderText={`Delete ${budgetClassification.budgetLineNickname}`}
                    modalFooterButtonText="Yes, delete"
                    modalFooterButtonIsLoading={isBudgetClassificationDeletionLoading}
                    danger
                >
                    <div className="text-dark font-weight-bold mb-1">
                        {`Are you sure you want to delete ${budgetClassification.budgetLineNickname}?`}
                    </div>
                    <div>Your work will not be able to be recovered.</div>
                </ConfirmationModal>
            </RenderIf>
        </>
    );
};

export default UsesTableBudgetLine;
